/**
 * Cimage Component
 * Unified image renderer with explicit static/dynamic backing, optional
 * loading skeletons, fallback source handling, and shared image entrance motion.
 */
'use client';
import type { CSSProperties, HTMLAttributeReferrerPolicy, ImgHTMLAttributes, ReactElement, ReactEventHandler, } from 'react';
import { useMemo } from 'react';
import type { Transition } from 'framer-motion';
import { RadiusSize } from '@/theme/radius';
import { TRANSITIONS } from '@/utils/animation';
import { CimageContent } from './CimageContent';
import { CIMAGE_ANIMATION, CIMAGE_DECODING_ASYNC, CIMAGE_LOADING_LAZY, CIMAGE_OBJECT_FIT, type CimageAnimation, type CimageObjectFit, } from './cimage-constants';
import { buildImageStyle, buildRootStyle, getImageDimensionModel, getImageMotionState, objectFitFromStyle, resolveRadiusValue, } from './cimage-model';
import { useCimageResourceState } from './useCimageResourceState';
export { CIMAGE_ANIMATION, CIMAGE_OBJECT_FIT } from './cimage-constants';
export type { CimageAnimation, CimageObjectFit } from './cimage-constants';
type CimageLoading = NonNullable<ImgHTMLAttributes<HTMLImageElement>['loading']>;
type CimageDecoding = NonNullable<ImgHTMLAttributes<HTMLImageElement>['decoding']>;
export interface CimageProps {
    src: string;
    alt: string;
    width?: number | string;
    height?: number | string;
    aspectRatio?: number;
    objectFit?: CimageObjectFit;
    radius?: number | RadiusSize;
    className?: string;
    style?: CSSProperties;
    static?: boolean;
    fallbackSrc?: string;
    loading?: CimageLoading;
    decoding?: CimageDecoding;
    referrerPolicy?: HTMLAttributeReferrerPolicy;
    showSkeleton?: boolean;
    skeletonRadius?: number | RadiusSize;
    animation?: CimageAnimation;
    transition?: Transition;
    title?: string;
    draggable?: boolean;
    priority?: boolean;
    sizes?: string;
    unoptimized?: boolean;
    ariaHidden?: boolean;
    onLoad?: ReactEventHandler<HTMLImageElement>;
    onError?: ReactEventHandler<HTMLImageElement>;
}
export function Cimage({ src, alt, width, height, aspectRatio, objectFit, radius, className, style, static: useStatic = false, fallbackSrc, loading = CIMAGE_LOADING_LAZY, decoding = CIMAGE_DECODING_ASYNC, referrerPolicy, showSkeleton = false, skeletonRadius, animation = CIMAGE_ANIMATION.FadeBlur, transition = TRANSITIONS.quick, title, draggable, priority, sizes, unoptimized, ariaHidden, onLoad, onError, }: CimageProps): ReactElement {
    const { handleError, handleLoad, isLoaded, renderedSrc, setImageRef, } = useCimageResourceState({
        fallbackSrc,
        onError,
        onLoad,
        src,
    });
    const resolvedObjectFit = objectFit ?? objectFitFromStyle(style) ?? CIMAGE_OBJECT_FIT.Cover;
    const radiusValue = resolveRadiusValue(radius);
    const skeletonRadiusValue = resolveRadiusValue(skeletonRadius ?? radius);
    const dimensionModel = useMemo(() => getImageDimensionModel({ aspectRatio, height, width }), [aspectRatio, height, width]);
    const dimensionHeightValue = dimensionModel.heightValue;
    const dimensionWidthValue = dimensionModel.widthValue;
    const canUseOptimizedStatic = useStatic && (dimensionModel.hasNumericDims || sizes != null);
    const shouldShowSkeleton = showSkeleton && !isLoaded;
    const motionState = getImageMotionState({ animation, isLoaded });
    const rootStyle = useMemo<CSSProperties>(() => buildRootStyle({
        aspectRatio,
        heightValue: dimensionHeightValue,
        radiusValue,
        showSkeleton,
        style,
        widthValue: dimensionWidthValue,
    }), [
        aspectRatio,
        dimensionHeightValue,
        dimensionWidthValue,
        radiusValue,
        showSkeleton,
        style,
    ]);
    const imageStyle = useMemo<CSSProperties>(() => buildImageStyle({
        style,
        width,
        height,
        aspectRatio,
        objectFit: resolvedObjectFit,
    }), [aspectRatio, height, resolvedObjectFit, style, width]);
    return (
    <span className={className} style={rootStyle}>
      <CimageContent alt={alt} ariaHidden={ariaHidden} canUseOptimizedStatic={canUseOptimizedStatic} decoding={decoding} dimensionModel={dimensionModel} draggable={draggable} imageAnimate={motionState.imageAnimate} imageExit={motionState.imageExit} imageInitial={motionState.imageInitial} imageStyle={imageStyle} loading={loading} objectFit={resolvedObjectFit} onError={handleError} onLoad={handleLoad} priority={priority} referrerPolicy={referrerPolicy} renderedSrc={renderedSrc} setImageRef={setImageRef} shouldShowSkeleton={shouldShowSkeleton} skeletonRadiusValue={skeletonRadiusValue} sizes={sizes} title={title} transition={transition} unoptimized={unoptimized}/>
    </span>
    );
}
